import { Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ValidationError } from "@common/validationError.ts";
import StepSegment from "../../../../processingActivity/stepSegment/StepSegment";
import { ContractModel } from "../../Contract.types";
import { useTranslation } from "@localization/useTranslation.ts";
import useCounterPartiesStepHooks from "./CounterPartiesStep.hooks";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import { ContractStandardField, getStandardFieldTranslationKey } from "../../../../entityTemplates/EntityTemplate.types";
import { contentResponsiveSize, useContentSize } from "../../../../main/content/ContentSizeContext";
import ContractPartyList from "./contractPartyList/ContractPartyList";
import { ContractPartyKind } from "./CounterPartiesStep.types";

export interface ICounterPartiesStepProps {
    contractId: string;
    contract: ContractModel;
    validationErrors: Array<ValidationError>;
    readOnly: boolean;
}

function CounterPartiesStep(props: ICounterPartiesStepProps) {
    const {
        expandedId,
        legalEntityData,
        internalOptions,
        externalOptions,
        internalParties,
        externalParties,
        legalEntityIsLoading,
        onExpandClick,
        onExpand,
        addCounterParty,
        removeCounterParty,
        isAddingCounterParty,
        onCounterPartyChange,
        onSubContractorChange,
    } = useCounterPartiesStepHooks(props);
    const { translateString } = useTranslation();
    const { breakpoint } = useContentSize();

    const columnSize = contentResponsiveSize(breakpoint, { sm: 12, md: 12, lg: 6 });
    const selectedLegalEntities = props.contract.counterParties.filter((x) => !isNullOrWhitespace(x.legalEntityId)).map((x) => x.legalEntityId!);
    const sharedListProps = {
        contractId: props.contractId,
        contractName: props.contract.name,
        legalEntityData,
        legalEntityIsLoading,
        validationErrors: props.validationErrors,
        selectedLegalentities: selectedLegalEntities,
        expandedId,
        onExpandClick,
        onExpand,
        onAddParty: addCounterParty,
        isAddingParty: isAddingCounterParty,
        onRemoveParty: removeCounterParty,
        onCounterPartyChange,
        onSubContractorChange,
        readOnly: props.readOnly,
    };

    return (
        <StepSegment size="large">
            <Grid container spacing={2}>
                <Grid size={columnSize}>
                    <ContractPartyList {...sharedListProps} kind={ContractPartyKind.Internal} parties={internalParties} partyOptions={internalOptions} />
                </Grid>
                <Grid size={columnSize}>
                    <ContractPartyList {...sharedListProps} kind={ContractPartyKind.External} parties={externalParties} partyOptions={externalOptions} />
                </Grid>
            </Grid>

            {props.validationErrors.find((e) => e.field === getStandardFieldTranslationKey(ContractStandardField.CounterParties)) && (
                <Typography
                    sx={(theme) => ({
                        fontSize: theme.typography.pxToRem(12),
                        color: theme.palette.error.main,
                        textAlign: "center",
                        marginTop: theme.spacing(2),
                    })}
                >
                    {translateString("contractPartiesRequired")}
                </Typography>
            )}
        </StepSegment>
    );
}

export default CounterPartiesStep;
